import { Stack } from '@chakra-ui/react';
import { setupI18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { useMemo, useRef } from 'react';

import { ChatInput } from '#modules/chat/components/Chats/ChatInput';
import { useProfile } from '#modules/user/features/profile/hooks/useProfile';
import { messages as enMessages } from '#features/i18n/locales/en/messages.po';
import { messages as ruMessages } from '#features/i18n/locales/ru/messages.po';

/** Isolated composer QA: no messages, typing events or profile mutations leave this page. */
export const Route = createFileRoute('/{-$locale}/_layout/preview/chat-contacts')({
  ssr: false,
  beforeLoad: () => {
    if (!import.meta.env.DEV) throw notFound();
  },
  validateSearch: (search: Record<string, unknown>) => ({
    lang: search.lang === 'en' ? ('en' as const) : ('ru' as const),
    count:
      search.count === 0 || search.count === '0'
        ? 0
        : search.count === 1 || search.count === '1'
          ? 1
          : 4,
    blocked: search.blocked === true || search.blocked === 'true',
    reply: search.reply === true || search.reply === 'true',
  }),
  head: () => ({ meta: [{ name: 'robots', content: 'noindex, nofollow' }] }),
  component: ContactsPreview,
});

const noop = () => {};

function ContactsPreview() {
  const { lang, count, blocked, reply } = Route.useSearch();
  const { profile } = useProfile();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const i18n = useMemo(
    () => setupI18n({ locale: lang, messages: { en: enMessages, ru: ruMessages } }),
    [lang]
  );
  const client = useMemo(() => {
    const isolated = new QueryClient();
    const defaults = isolated.defaultQueryOptions.bind(isolated);
    isolated.defaultQueryOptions = (options) => ({
      ...defaults(options),
      enabled: false,
      staleTime: Infinity,
      refetchInterval: false,
    });
    // Retain the viewer's identity; only the local contact examples differ.
    isolated.setQueryData(['me'], {
      ...(profile ?? { __typename: 'CurrentUser', id: 'qa-chat-contacts', displayName: 'QA' }),
      oauth: {
        ...(count > 0
          ? {
              discord: {
                id: 'qa-discord',
                displayName: 'PlayerWithAVeryLongDiscordDisplayName',
                visible: true,
              },
            }
          : {}),
        ...(count > 1
          ? {
              steam: { id: '76561198000000000', displayName: 'Player', visible: true },
              epicGames: {
                id: 'qa-epic-player-with-a-long-account-id',
                displayName: 'Player',
                visible: true,
              },
              riotGames: {
                id: 'qa-riot',
                displayName: 'PlayerWithALongName#TEAMPLAY',
                visible: true,
              },
            }
          : {}),
      },
    });
    return isolated;
  }, [profile, count]);

  return (
    <I18nProvider i18n={i18n}>
      <QueryClientProvider client={client}>
        <Stack w="100%" minW={0} maxW="3xl" mx="auto" minH="60vh" justifyContent="flex-end" gap={0}>
          <ChatInput
            key={`${lang}-${count}-${blocked}-${reply}`}
            chatId="qa-chat-contacts"
            draftKey="qa-chat-contacts"
            replyTo={
              reply
                ? { text: 'PlayerWithAVeryLongName: Counter-Strike 2 Premier Competitive Edition' }
                : null
            }
            onCancelReply={noop}
            textareaRef={textareaRef}
            onSend={async () => false}
            isSending={false}
            isBlocked={blocked}
            blockedByPeer={blocked}
            isTyping={false}
            onTyping={noop}
            onStopTyping={noop}
          />
        </Stack>
      </QueryClientProvider>
    </I18nProvider>
  );
}
